import React from "react";
import { Card, CardBody, CardHeader, Button, Progress } from "reactstrap";
import { useSelector } from "react-redux";
import { IApplicationState } from "../../redux/reducers";
import "../../assets/scss/pages/admin/_homeAdmin.scss";
import {
	FaBell,
	FaBoxOpen,
	FaUsers,
	FaUserFriends,
	FaDollarSign,
	FaHeartbeat,
	FaExclamationTriangle,
	FaBullseye,
	FaUtensils,
	FaArchive,
	FaLeaf,
	FaBrain,
	FaHistory,
	FaComments,
	FaDownload,
	FaFileAlt,
	FaUserCog,
	FaDatabase,
	FaChartLine
} from "react-icons/fa";

const HomeAdmin: React.FC = () => {
	// Redux state
	const ingredientsState = useSelector((state: IApplicationState) => state.ingredient.ingredients);
	const productsState = useSelector((state: IApplicationState) => state.admin.products);

	// Dynamic values
	const totalIngredients = ingredientsState?.pagination?.total || 0;
	const verifiedIngredients = ingredientsState?.list?.filter((i) => i.is_verified)?.length || 0;

	const productsCataloged = productsState?.pagination?.total || 0;
	const pendingProducts = productsState?.products?.filter((p) => !p.is_verified)?.length || 0;

	// Placeholders
	const packagingTypes = 1247;
	const sustainablePackaging = 892;
	const aiRecommendations = 3456;
	const aiAccuracy = "94.2%";

	return (
		<div className="dashboard container-fluid py-4">
			{/* Header */}
			<div className="d-flex justify-content-between align-items-center mb-4">
				<div>
					<h2>Home</h2>
					<p className="text-muted">
						Internal team dashboard - Monitor operations, track performance, and manage platform
						health.
					</p>
				</div>
				<Button color="light" className="position-relative">
					<FaBell />
					<span className="badge bg-danger position-absolute top-0 start-100 translate-middle p-1 rounded-circle"></span>
				</Button>
			</div>

			{/* Top KPIs */}
			<div className="row g-3 mb-4">
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaDollarSign />
							</div>
							<h6>Annual Recurring Revenue</h6>
							<h3>$1,257,345.89</h3>
							<small className="text-success">+20.1% from last month</small>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaBoxOpen />
							</div>
							<h6>Active Suppliers</h6>
							<h3>2,350</h3>
							<small className="text-success">+180.1% from last month</small>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaUsers />
							</div>
							<h6>App Users</h6>
							<h3>12,234</h3>
							<small className="text-success">+19% from last month</small>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaUserFriends />
							</div>
							<h6>B2B Clients</h6>
							<h3>573</h3>
							<small className="text-info">+201 since last hour</small>
						</CardBody>
					</Card>
				</div>
			</div>

			{/* Middle Section */}
			<div className="row g-3 mb-4">
				<div className="col-md-4">
					<Card className="shadow-sm h-100">
						<CardHeader>
							<FaHeartbeat className="me-2 text-danger" /> System Health
						</CardHeader>
						<CardBody>
							<p>
								API Response Time: <span className="badge bg-light text-dark">142ms</span>
							</p>
							<p>
								Database Performance: <span className="badge bg-success">Optimal</span>
							</p>
							<p>
								Server Uptime: <span className="badge bg-light text-dark">99.9%</span>
							</p>
							<p>
								Active Sessions: <span className="badge bg-light text-dark">1,247</span>
							</p>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-4">
					<Card className="shadow-sm h-100">
						<CardHeader>
							<FaExclamationTriangle className="me-2 text-warning" /> Active Alerts
						</CardHeader>
						<CardBody>
							<ul className="list-unstyled">
								<li className="text-danger">● High memory usage on server-03</li>
								<li className="text-warning">● Slow query detected in products DB</li>
								<li className="text-orange">● SSL certificate expires in 7 days</li>
							</ul>
							<Button color="light" size="sm">
								View All Alerts
							</Button>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-4">
					<Card className="shadow-sm h-100">
						<CardHeader>
							<FaBullseye className="me-2 text-primary" /> Team Goals
						</CardHeader>
						<CardBody>
							<p>Monthly Revenue Target</p>
							<Progress value={87} />
							<p className="mt-3">Customer Acquisition</p>
							<Progress value={92} color="success" />
							<p className="mt-3">Data Quality Score</p>
							<Progress value={78} color="info" />
						</CardBody>
					</Card>
				</div>
			</div>

			{/* Stats Row (Dynamic) */}
			<div className="row g-3 mb-4">
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaUtensils />
							</div>
							<h6>Total Ingredients</h6>
							<h3>{totalIngredients.toLocaleString()}</h3>
							<small className="text-success">+234 this week</small>
							<br />
							<small className="text-muted">Verified: {verifiedIngredients}</small>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaArchive />
							</div>
							<h6>Products Cataloged</h6>
							<h3>{productsCataloged.toLocaleString()}</h3>
							<small className="text-success">+156 this week</small>
							<br />
							<small className="text-warning">Pending: {pendingProducts}</small>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaLeaf />
							</div>
							<h6>Packaging Types</h6>
							<h3>{packagingTypes.toLocaleString()}</h3>
							<small className="text-success">+23 this week</small>
							<br />
							<small className="text-info">Sustainable: {sustainablePackaging}</small>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-3">
					<Card className="h-100 shadow-sm kpi-card">
						<CardBody>
							<div className="icon">
								<FaBrain />
							</div>
							<h6>AI Recommendations</h6>
							<h3>{aiRecommendations.toLocaleString()}</h3>
							<small>Generated today</small>
							<br />
							<small className="text-purple">Accuracy: {aiAccuracy}</small>
						</CardBody>
					</Card>
				</div>
			</div>

			{/* Recent Activity + Communications */}
			<div className="row g-3 mb-4">
				<div className="col-md-6">
					<Card className="shadow-sm h-100">
						<CardHeader>
							<FaHistory className="me-2" /> Recent Platform Activity
						</CardHeader>
						<CardBody>
							<ul className="list-unstyled mb-0">
								<li>
									John Doe updated 47 ingredient profiles{" "}
									<small className="text-muted">2m ago</small>
								</li>
								<li>
									Sarah Miller added new supplier: GreenPack Co.{" "}
									<small className="text-muted">15m ago</small>
								</li>
								<li>
									AI System processed 1,234 new products{" "}
									<small className="text-muted">1h ago</small>
								</li>
								<li>
									Mike Johnson ran data quality audit <small className="text-muted">3h ago</small>
								</li>
							</ul>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-6">
					<Card className="shadow-sm h-100">
						<CardHeader>
							<FaComments className="me-2" /> Team Communications
						</CardHeader>
						<CardBody>
							<div className="p-2 mb-2 bg-light border-start border-primary">
								System Maintenance Scheduled
							</div>
							<div className="p-2 mb-2 bg-light border-start border-success">
								New Feature: Bulk Import
							</div>
							<div className="p-2 mb-2 bg-light border-start border-warning">
								Data Quality Review
							</div>
						</CardBody>
					</Card>
				</div>
			</div>

			{/* Quick Actions + Upcoming Tasks */}
			<div className="row g-3 mb-4">
				<div className="col-md-6">
					<Card className="shadow-sm h-100">
						<CardHeader>
							<FaDownload className="me-2" /> Quick Actions
						</CardHeader>
						<CardBody className="d-flex flex-wrap gap-2">
							<Button color="light">
								<FaDownload className="me-2" /> Bulk Import
							</Button>
							<Button color="light">
								<FaFileAlt className="me-2" /> Generate Report
							</Button>
							<Button color="light">
								<FaUserCog className="me-2" /> User Management
							</Button>
							<Button color="light">
								<FaDatabase className="me-2" /> Export Data
							</Button>
						</CardBody>
					</Card>
				</div>
				<div className="col-md-6">
					<Card className="shadow-sm h-100">
						<CardHeader>
							<FaBullseye className="me-2 text-info" /> Upcoming Tasks
						</CardHeader>
						<CardBody>
							<p>
								Weekly Data Backup <small className="text-muted">Tonight</small>
							</p>
							<p>
								Supplier Review Meeting <small className="text-muted">Tomorrow</small>
							</p>
							<p>
								AI Model Retraining <small className="text-muted">Friday</small>
							</p>
						</CardBody>
					</Card>
				</div>
			</div>

			{/* Analytics */}
			<Card className="shadow-sm mb-4">
				<CardHeader>
					<FaChartLine className="me-2" /> Platform Performance Analytics
				</CardHeader>
				<CardBody className="text-center text-muted" style={{ height: "200px" }}>
					<FaChartLine size={32} className="mb-2" />
					<p>Advanced Analytics Dashboard - Real-time metrics and system performance</p>
				</CardBody>
			</Card>
		</div>
	);
};

export default HomeAdmin;
